import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Pin, PinOff, LogOut } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { getVisibleSections } from './navigation';
import type { UserRole } from './navigation';
import { useUIStore } from '../../store/uiStore';

export function Sidebar() {
  const { user, logout } = useAuth();
  const { sidebarPinned, toggleSidebarPin } = useUIStore();
  const [hovered, setHovered] = useState(false);

  const expanded = sidebarPinned || hovered;
  const role: UserRole = (user?.role as UserRole) ?? 'pm';
  const sections = getVisibleSections(role);

  return (
    <aside
      className="fixed inset-y-0 left-0 z-30 flex flex-col transition-all duration-200 ease-out"
      style={{
        width: expanded ? 'var(--sidebar-expanded)' : 'var(--sidebar-collapsed)',
        background: 'var(--surface-raised)',
        borderRight: '1px solid var(--border-subtle)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Logo */}
      <div
        className="flex items-center h-[52px] shrink-0 overflow-hidden"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}
      >
        <div className="w-16 shrink-0 flex items-center justify-center">
          <div
            className="w-7 h-7 rounded-[6px] flex items-center justify-center text-xs font-bold"
            style={{ background: 'var(--accent-primary)', color: 'var(--surface-base)' }}
          >
            NW
          </div>
        </div>
        <span
          className="text-sm font-semibold whitespace-nowrap transition-opacity duration-150"
          style={{
            color: 'var(--text-primary)',
            opacity: expanded ? 1 : 0,
          }}
        >
          Webtoon MOS
        </span>
        {expanded && (
          <button
            onClick={toggleSidebarPin}
            className="ml-auto mr-3 p-1 rounded-[4px] transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          >
            {sidebarPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2">
        {sections.map((section) => (
          <div key={section.section} className="mb-1">
            <p
              className="px-4 mb-0.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap transition-opacity duration-150"
              style={{
                color: 'var(--text-tertiary)',
                opacity: expanded ? 1 : 0,
                height: expanded ? 'auto' : 0,
                overflow: 'hidden',
              }}
            >
              {section.section}
            </p>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    flex items-center h-[var(--row-height)] transition-colors duration-100 group relative
                    ${isActive
                      ? 'text-[var(--accent-primary)]'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div
                          className="absolute left-0 top-0 bottom-0 w-0.5 rounded-r-full"
                          style={{ background: 'var(--accent-primary)' }}
                        />
                      )}
                      <div className="w-16 shrink-0 flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span
                        className="text-[13px] font-medium whitespace-nowrap transition-opacity duration-150"
                        style={{ opacity: expanded ? 1 : 0 }}
                      >
                        {item.name}
                      </span>
                      {isActive && (
                        <div
                          className="absolute inset-0 rounded-r-[0px] pointer-events-none"
                          style={{ background: 'rgba(34,201,151,0.05)' }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User / Logout */}
      <div
        className="shrink-0 py-2"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <button
          onClick={logout}
          className="flex items-center w-full h-[var(--row-height)] transition-colors duration-100 group"
          style={{ color: 'var(--text-secondary)' }}
          title="Log out"
        >
          <div className="w-16 shrink-0 flex items-center justify-center">
            <LogOut className="w-4 h-4" />
          </div>
          <div
            className="flex flex-col items-start transition-opacity duration-150 overflow-hidden"
            style={{ opacity: expanded ? 1 : 0 }}
          >
            <span className="text-[11px] whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
              {user?.email}
            </span>
            <span className="text-[13px] font-medium whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
              Log out
            </span>
          </div>
        </button>
      </div>
    </aside>
  );
}
